import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, ScrollText, Save, Calculator } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

const VOUCHER_TYPES = [
  { key: "sales",       label: "Sales" },
  { key: "purchase",    label: "Purchase" },
  { key: "payment",     label: "Payment" },
  { key: "receipt",     label: "Receipt" },
  { key: "contra",      label: "Contra" },
  { key: "journal",     label: "Journal" },
  { key: "debit_note",  label: "Debit Note" },
  { key: "credit_note", label: "Credit Note" },
];

export function VoucherList() {
  const { active } = useCompany();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const type = params.get("type") || "";

  const load = async () => {
    if (!active) return;
    const p = new URLSearchParams();
    if (type) p.set("type", type);
    const { data } = await api.get(`/vouchers?${p.toString()}`);
    setRows(data.vouchers || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, type]);

  const filtered = rows.filter((v) => !q ||
    (v.number || "").toLowerCase().includes(q.toLowerCase()) ||
    (v.narration || "").toLowerCase().includes(q.toLowerCase())
  );
  const cur = active?.base_currency || "INR";

  return (
    <div className="space-y-4" data-testid="vouchers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Vouchers</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Voucher Register</h1>
        </div>
        <button data-testid="new-voucher-btn" onClick={() => navigate(`/app/vouchers/new${type ? "?type=" + type : ""}`)} className="btn-primary w-full sm:w-auto justify-center">
          <Plus size={14}/> New Voucher
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[#E2E8F0]">
        <button data-testid="tab-voucher-all" onClick={() => setParams({})}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${!type ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#64748B]"}`}>
          All
        </button>
        {VOUCHER_TYPES.map((v) => (
          <button key={v.key} data-testid={`tab-voucher-${v.key}`} onClick={() => setParams({ type: v.key })}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${type === v.key ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#64748B]"}`}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="card-surface p-4">
        <input className="input-clean mb-3" placeholder="Search number or narration" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 400 }}/>
        {filtered.length === 0 ? (
          <div className="text-center py-10">
            <ScrollText size={40} className="mx-auto text-[#CBD5E1] mb-2"/>
            <p className="text-sm text-[#64748B]">No vouchers yet.</p>
          </div>
        ) : (
          <table className="table-clean">
            <thead><tr><th>Number</th><th>Type</th><th>Date</th><th>Narration</th><th className="text-right">Total</th></tr></thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} data-testid={`voucher-row-${v.id}`}>
                  <td className="font-mono-data text-[#1E3A8A] font-semibold">{v.number}</td>
                  <td><span className="chip chip-info text-[10px] uppercase">{v.type}</span></td>
                  <td className="font-mono-data text-xs">{v.date}</td>
                  <td className="text-sm">{v.narration}</td>
                  <td className="text-right font-mono-data font-semibold">{fmtMoney(v.total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function VoucherCreate() {
  const { active } = useCompany();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [type, setType] = useState(params.get("type") || "sales");
  const [ledgers, setLedgers] = useState([]);
  const [parties, setParties] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    narration: "", reference: "", party_id: "",
    lines: [
      { ledger_id: "", dr: 0, cr: 0, narration: "" },
      { ledger_id: "", dr: 0, cr: 0, narration: "" },
    ],
  });

  useEffect(() => {
    if (!active) return;
    Promise.all([api.get("/ledgers"), api.get("/parties")]).then(([l, p]) => {
      setLedgers(l.data.ledgers || []);
      setParties(p.data.parties || []);
    });
  }, [active]);

  const totals = useMemo(() => {
    const dr = form.lines.reduce((s, l) => s + (parseFloat(l.dr) || 0), 0);
    const cr = form.lines.reduce((s, l) => s + (parseFloat(l.cr) || 0), 0);
    return { dr: dr.toFixed(2), cr: cr.toFixed(2), diff: (dr - cr).toFixed(2), balanced: Math.abs(dr - cr) < 0.01 && dr > 0 };
  }, [form.lines]);

  const addLine = () => setForm((f) => ({
    ...f, lines: [...f.lines, { ledger_id: "", dr: 0, cr: 0, narration: "" }],
  }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm((f) => ({
    ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l),
  }));

  const submit = async () => {
    if (!totals.balanced) { toast.error(`Voucher not balanced. Dr ${totals.dr} vs Cr ${totals.cr}`); return; }
    const valid = form.lines.filter((l) => l.ledger_id && ((parseFloat(l.dr) || 0) > 0 || (parseFloat(l.cr) || 0) > 0));
    if (valid.length < 2) { toast.error("At least 2 balanced lines required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/vouchers", { ...form, type, lines: valid });
      toast.success(`${data.number} posted`);
      navigate("/app/vouchers?type=" + type);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post voucher"); }
    finally { setSaving(false); }
  };

  const cur = active?.base_currency || "INR";

  return (
    <div className="space-y-4" data-testid="voucher-create-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">New Voucher</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Post Voucher</h1>
        </div>
        <button data-testid="post-voucher-btn" onClick={submit} disabled={saving || !totals.balanced} className="btn-primary w-full sm:w-auto justify-center">
          <Save size={14}/> {saving ? "Posting…" : "Post Voucher"}
        </button>
      </div>

      <div className="card-surface p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div><label className="label-field">Voucher Type *</label>
          <select data-testid="voucher-type" className="input-clean" value={type} onChange={(e) => setType(e.target.value)}>
            {VOUCHER_TYPES.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select></div>
        <div><label className="label-field">Date *</label>
          <input data-testid="voucher-date" type="date" className="input-clean" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}/></div>
        <div><label className="label-field">Party (optional)</label>
          <select data-testid="voucher-party" className="input-clean" value={form.party_id} onChange={(e) => setForm({ ...form, party_id: e.target.value })}>
            <option value="">—</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
        <div><label className="label-field">Reference</label>
          <input className="input-clean" placeholder="Cheque #, txn id…" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}/></div>
        <div className="md:col-span-4"><label className="label-field">Narration</label>
          <input data-testid="voucher-narration" className="input-clean" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })}
            placeholder="e.g. Being cash sales to walk-in customer"/></div>
      </div>

      {/* Lines */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Double-Entry Lines</h3>
          <button data-testid="add-line-btn" onClick={addLine} className="btn-ghost text-xs"><Plus size={12}/> Add Line</button>
        </div>
        <table className="table-clean">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Ledger</th>
              <th className="text-right">Debit</th>
              <th className="text-right">Credit</th>
              <th>Narration</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.lines.map((ln, i) => (
              <tr key={i} data-testid={`voucher-line-${i}`}>
                <td>
                  <select data-testid={`line-ledger-${i}`} className="input-clean text-sm" value={ln.ledger_id} onChange={(e) => updateLine(i, { ledger_id: e.target.value })}>
                    <option value="">Select ledger…</option>
                    {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.group_name})</option>)}
                  </select>
                </td>
                <td>
                  <input data-testid={`line-dr-${i}`} type="number" step="0.01" className="input-clean text-sm font-mono-data text-right"
                    value={ln.dr} onChange={(e) => updateLine(i, { dr: parseFloat(e.target.value) || 0, cr: 0 })}/>
                </td>
                <td>
                  <input data-testid={`line-cr-${i}`} type="number" step="0.01" className="input-clean text-sm font-mono-data text-right"
                    value={ln.cr} onChange={(e) => updateLine(i, { cr: parseFloat(e.target.value) || 0, dr: 0 })}/>
                </td>
                <td>
                  <input className="input-clean text-sm" value={ln.narration} onChange={(e) => updateLine(i, { narration: e.target.value })}/>
                </td>
                <td>
                  {form.lines.length > 2 && (
                    <button onClick={() => removeLine(i)} data-testid={`remove-line-${i}`} className="text-[#DC2626]"><Trash2 size={14}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="font-bold text-right">Totals</td>
              <td className="text-right font-mono-data font-bold" data-testid="total-dr">{fmtMoney(totals.dr, cur)}</td>
              <td className="text-right font-mono-data font-bold" data-testid="total-cr">{fmtMoney(totals.cr, cur)}</td>
              <td colSpan={2}>
                <span className={`chip ${totals.balanced ? "chip-success" : "chip-danger"} text-xs`} data-testid="balance-chip">
                  <Calculator size={11}/>
                  {totals.balanced ? "Balanced" : `Off by ${fmtMoney(totals.diff, cur)}`}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default VoucherList;
